import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProductOnUser, ProductsList } from 'common-ts/dist/models/Product';
import { CartState, CartStateItem, RegularSizeValue, SizeType, ProductSpotlight, RegularSizeValueShortName } from 'common-ts/dist/models/Cart';
import axios, { AxiosRequestConfig } from 'axios';
import { RootState } from '../store';
import { OrderItem } from 'common-ts/dist/models/Orders';
import { BodyMeasurements, BodyMeasurementsForSize, BodyMeasurementsKey, CalculateClothBodyMeasurementsFromBodyMeasurements, CalculateClothingMeasurementsFromBodyMeasurements, ClothingType, FitType, UserBodyMeasurements, UserClothTypeSpecificBodyMeasurements, UserPersonalSizeInput } from 'common-ts/dist/models/UserMeasurements';
import { Address } from 'common-ts/dist/models/Address';
import { Mixpanel } from '../mixpanel';
import hash from 'object-hash';
import { loadMeasurementsFromLocalStorage, postUserPersonalSizeInput } from './MeasurementsRedux';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { sendWebsiteEventMessage } from '../telegrambotevents';
import { setRedirectLink } from './MailingPreferenceRedux';
import { setRedirectOverlay } from './UIOverlayQRRepurchaseRedux';
import { getOfflineTrueStock } from './ProductsListRedux';
import { getSessionAttribution, getAdTargetedProductIds } from '../utils/sessionAttribution';

export const retryGetRequest = async (config: AxiosRequestConfig,  maxRetries: number) => {
  let result = null;
  var retries: number = 0;

  while (retries < maxRetries) {
    try {
      result = await axios(config);
      break;
    } catch (error) {
      console.log("Retry number: " + retries);
    }
    retries = retries + 1;
  }
  
  if (retries >= maxRetries) {
    console.log(`Too many  request retries.`)
  }
  return result;
}

// Define the initial state using that type
const initialState: CartState = {
  cartProductList: 
    localStorage.getItem('cartProductList') !== null ? 
    JSON.parse(localStorage.getItem('cartProductList') as string) : 
    [],
  addressName: undefined,
  address: undefined,
  kodePos: undefined,
  provinsi: undefined,
  kabupaten: undefined,
  kecamatan: undefined,
  kotaKecamatanText: undefined,
  keterangan: undefined,
  phoneNumber: undefined,
  destinationObject: undefined,
  destinationKiriminAjaObject: undefined,
  courierSelectionObject: undefined,
  courierWeight: undefined,
  shipmentPrice: undefined,
  shipmentPricePaid: undefined,
  estimasiPengiriman: undefined,
  couponKey: localStorage.getItem('coupon') ? 
  JSON.parse(localStorage.getItem('coupon') as string)?.couponKey : 
  undefined,
  coupon: localStorage.getItem('coupon') ? 
  JSON.parse(localStorage.getItem('coupon') as string) : 
  undefined,
  orderStatus: 'unsubmitted',
  orderData: undefined,
  userTriggerCheckout: false,

  // Livestream Discounts
  productSpotlightList: [],

  cartProductListHash: '',
  canAddItemDict: {},

  courierOptions: undefined,
  courierOptionsFetchStatus: APIRequestStatus.Idle,
  
  couponFetchStatus: APIRequestStatus.Idle,

  paymentToken: "",
  paymentTokenFetchStatus: APIRequestStatus.Idle,
}

function getCookieValueByName(name: string) {
  var match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : "";
}

export const submitOrderRequest = createAsyncThunk(
  'cart/submitOrderRequest', 
  async (payload, thunkAPI) => {
    const url = 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/order';

    const rootState = (thunkAPI.getState() as RootState);
    const idToken = rootState.account.idToken;

    const cart = rootState.cart;
    const productsList = rootState.productsList;
    const userBodyMeasurementsDict = rootState.userMeasurementsList.userBodyMeasurementsDictionary;
    const userClothingMeasurementsDict = rootState.userMeasurementsList.userClothingMeasurementsDictionary;

    const resultObj = calculateCompleteCartInvoice(cart, rootState.productsList, userBodyMeasurementsDict, userClothingMeasurementsDict);
    const totalCartAmount = resultObj.totalAmountObj.discountedPriceAmountWithPromotions;
    const totalCartAmountWithShipment = resultObj.totalAmountObj.discountedPriceAmountWithPromotionsAndShipment;

    if (cart.shipmentPrice === undefined) {
      console.log("ERROR! SHIPMENT PRICE HAS TO ALWAYS EXIST BEFORE THIS IS CALLED");
      return;
    }

    // Prepare user personal size last input for the current user sub
    const userPersonalSizeInputNewEntry: UserPersonalSizeInput = {
      userSub: rootState.account.sub,
      name: rootState.account.name,
      sizeType: "regular",
    }

    let cartProductListWithProductDetail: OrderItem[] = [];
    for (let key in cart.cartProductList) {
      const cartItem = cart.cartProductList[key];
      const productDetail = productsList.productsDictionary[cartItem.productId];
      const key2 = productDetail.clothingType + "-" + cartItem.fitType;
      const orderItem: OrderItem = {
        ...cartItem,
        productDetail: productDetail,
        userMeasurementsForItem: 'NOTSELECTED',
        labelNama: '',
      };
      if (cartItem.measurementIsBodyMeasurement) {
        if (orderItem.sizeType === 'personal') {
          // USE LATEST INSTEAD OF CORRECT VERSION
          let selectedObjectUpdateDateTime = userBodyMeasurementsDict[cartItem.measurementCreationDateTime][0];
          // for (let candidateObject of userBodyMeasurementsDict[cartItem.measurementCreationDateTime]) {
          //   if (candidateObject.updateDateTime === cartItem.measurementUpdateDateTime) {
          //     selectedObjectUpdateDateTime = candidateObject;
          //   }
          // }
          orderItem.userMeasurementsForItem = {
            ...selectedObjectUpdateDateTime,
            adjustedMeasurements: CalculateClothBodyMeasurementsFromBodyMeasurements(productDetail.clothingType, 
              cartItem.fitType, 
              (selectedObjectUpdateDateTime?.adjustedMeasurements ? 
                selectedObjectUpdateDateTime.adjustedMeasurements : 
                selectedObjectUpdateDateTime.generatedMeasurements) as any, RegularSizeValue.L, [])
          }
  
          userPersonalSizeInputNewEntry.sizeType = "personal";
          userPersonalSizeInputNewEntry.age = orderItem.userMeasurementsForItem.umur;
          userPersonalSizeInputNewEntry.height = orderItem.userMeasurementsForItem.height;
          userPersonalSizeInputNewEntry.weight = orderItem.userMeasurementsForItem.weight;
          userPersonalSizeInputNewEntry.bodyType = orderItem.userMeasurementsForItem.bodyType;
        }
        if (orderItem.sizeType === "regular") {
          orderItem.userMeasurementsForItem = {
            sizeType: SizeType.regular,
            regularSize: cartItem.regularSize,
            adjustedMeasurements: BodyMeasurementsForSize[cartItem.regularSize],
            nama: userPersonalSizeInputNewEntry?.name ? userPersonalSizeInputNewEntry.name : "User"
          };

          if (userPersonalSizeInputNewEntry.sizeType === "regular") {
            userPersonalSizeInputNewEntry.regularSize = cartItem.regularSize;
          }
        }
      } else {
        if (orderItem.sizeType === 'personal') {
          // FOR NOW, PERSONAL AND IS CLOTHING MEASUREMENT EQUALS DARI UKURAN BAJU FAVORIT TO UPDATE
          let selectedObjectUpdateDateTime = userClothingMeasurementsDict[key2][cartItem.measurementCreationDateTime][0];
          for (let candidateObject of userClothingMeasurementsDict[key2][cartItem.measurementCreationDateTime]) {
            if (candidateObject.updateDateTime === cartItem.measurementUpdateDateTime) {
              selectedObjectUpdateDateTime = candidateObject;
            }
          }
          orderItem.userMeasurementsForItem = selectedObjectUpdateDateTime;

          // userPersonalSizeInputNewEntry.sizeType = "personal";
          // userPersonalSizeInputNewEntry.age = orderItem.userMeasurementsForItem.umur;
          // userPersonalSizeInputNewEntry.height = orderItem.userMeasurementsForItem.height;
          // userPersonalSizeInputNewEntry.weight = orderItem.userMeasurementsForItem.weight;
          // userPersonalSizeInputNewEntry.bodyType = orderItem.userMeasurementsForItem.bodyType;

          // FOR NOW, PERSONAL AND IS CLOTHING MEASUREMENT EQUALS DARI UKURAN BAJU FAVORIT TO UPDATE
          if (userPersonalSizeInputNewEntry.sizeType === "regular") {
            userPersonalSizeInputNewEntry.regularSize = cartItem.regularSize;
          }
        }
        if (orderItem.sizeType === "regular") {
          orderItem.userMeasurementsForItem = {
            sizeType: SizeType.regular,
            regularSize: cartItem.regularSize,
            adjustedMeasurements: BodyMeasurementsForSize[cartItem.regularSize],
            nama: userPersonalSizeInputNewEntry?.name ? userPersonalSizeInputNewEntry.name : "User"
          };

          if (userPersonalSizeInputNewEntry.sizeType === "regular") {
            userPersonalSizeInputNewEntry.regularSize = cartItem.regularSize;
          }
        }
      }
      cartProductListWithProductDetail.push(orderItem);
    }

    // Calculate requestData
    var requestDataProducts = getRequestDataProducts(cart.cartProductList, userBodyMeasurementsDict, userClothingMeasurementsDict);
  

    let customerNumber = rootState.account.phoneNumber;

    customerNumber = customerNumber.replace(/[^\d]/g, '');
    while (customerNumber.startsWith("62") || customerNumber.startsWith("0")) {
      if (customerNumber.startsWith("62")) {
        customerNumber = customerNumber.substring(2);
      } else if (customerNumber.startsWith("0")) {
        customerNumber = customerNumber.substring(1);
      }
    }

    customerNumber = "62" + customerNumber;

    const jsonBody = {
      cartProductList: cartProductListWithProductDetail,
      addressName: cart.addressName,
      address: cart.address,
      provinsi: cart.provinsi,
      kabupaten: cart.kabupaten,
      kecamatan: cart.kecamatan,
      kodePos: cart.kodePos,
      kotaKecamatanText: cart.kotaKecamatanText,
      keterangan: cart.keterangan,
      phoneNumber: "+" + customerNumber,
      destinationObject: cart.destinationObject,
      destinationKiriminAjaObject: cart.destinationKiriminAjaObject,
      courierSelectionObject: cart.courierSelectionObject,
      courierWeight: cart.courierWeight,
      shipmentPrice: cart.shipmentPrice,
      shipmentPricePaid: cart.shipmentPricePaid,
      estimasiPengiriman: cart.estimasiPengiriman,
      couponKey: cart.couponKey,
      coupon: cart.coupon,
      totalCartAmount: totalCartAmount,
      totalPaymentAmount: totalCartAmountWithShipment,
      requestData: requestDataProducts,
      // Offline "true stock" mode: order provisioning excludes hutang stok.
      ...(getOfflineTrueStock() ? { trueStockCheck: true } : {}),
      orderTrackingDetails: {
        userSub: rootState.account.sub,
        email: rootState.account.email,
        phoneNumber: customerNumber,
        fbc: getCookieValueByName("_fbc"),
        fbp: getCookieValueByName("_fbp"),
      },
      orderName: rootState.account.name,
      // Session ad attribution (product scoring): lets the payment webhook tag PURCHASE
      // analytics events with the right stratum without any collection lookup server-side.
      sessionAttribution: (() => {
        const attribution = getSessionAttribution();
        return {
          sessionId: attribution.sessionId,
          hasAdMarkers: attribution.hasAdMarkers,
          adSource: attribution.adSource,
          adCollectionKey: attribution.adCollectionKey,
          adTargetedProductIds: getAdTargetedProductIds(
            cartProductListWithProductDetail.map((item) => item.productId),
            productsList.collectionsDictionary
          ),
        };
      })(),
    };

    const data = JSON.stringify(jsonBody);


    var config: AxiosRequestConfig = {
      method: 'post',
      url: url,
      headers: { 
        'Authorization': 'Bearer ' + idToken, 
      },
      data: data
    };

    const response = await axios(config);

    console.log(response);

    // Update user personal input data
    // We update the user personal size input measurements
    thunkAPI.dispatch(postUserPersonalSizeInput(userPersonalSizeInputNewEntry));

    return response.data;
  });

export const cancelUserOrders = createAsyncThunk('cart/cancelUserOrders', async (arg, thunkAPI) => {

  const rootState = (thunkAPI.getState() as RootState);
  const idToken = rootState?.account?.idToken;

  if (idToken) {
    axios({
        method: 'post',
        url: "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/orders-cancel-post",
        headers: { 
          'Authorization': 'Bearer ' + idToken, 
        }
    });
  }

  return {};
})

export const postCustomerNotification = createAsyncThunk('cart/postCustomerNotification', async (arg: any, thunkAPI) => {
  axios({
      method: 'post',
      url: "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/customer-notify",
      data: arg
  });

  return {};
})

export const getCurrentOrder = createAsyncThunk('cart/getCurrentOrder', async (payload, thunkAPI) => {
  const url = 'https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/order';

  const rootState = (thunkAPI.getState() as RootState);
  const idToken = rootState.account.idToken;

  const orderData = rootState.cart.orderData;

  var config: AxiosRequestConfig = {
    method: 'get',
    url: url + '?creationDateTime=' + orderData.creationDateTime,
    headers: { 
      'Authorization': 'Bearer ' + idToken, 
    }
  };

  let response = await axios(config);
  console.log("current order: ", response.data);

  let retryCount = 0;
  while (response.data.paymentStatus === 'unpaid' && retryCount < 5) {
    console.log("Payment status is still unpaid. Refetching in 1 s.");
    await new Promise((resolve) => setTimeout(resolve, 1000));
    retryCount = retryCount + 1;
    response = await axios(config);
    console.log("current order refetch: ", response.data);
  }

  return response.data;
})

export const submitCouponRequest = createAsyncThunk('cart/submitCouponRequest', async (payload: string, thunkAPI) => {
  const url = 'https://hx969cwq4f.execute-api.ap-southeast-1.amazonaws.com/CouponAPIProduction';
  const urlNotSignedIn = 'https://hx969cwq4f.execute-api.ap-southeast-1.amazonaws.com/CouponAPIProduction/coupons';

  const rootState = (thunkAPI.getState() as RootState);
  const isSignedIn = rootState.account.authState === "signedin";
  const idToken = rootState.account.idToken;

  var config: AxiosRequestConfig = {
    method: 'get',
    url: (isSignedIn ? url : urlNotSignedIn) + '?couponkey=' + payload,
    headers: isSignedIn ?
    { 
      'Authorization': 'Bearer ' + idToken, 
    } : undefined
  };

  let response = await axios(config);

  // We set if there are any thing
  if (response?.data?.redirectLink) {
    thunkAPI.dispatch(setRedirectLink({
      redirectLink: response.data.redirectLink,
      redirectUserSub: response.data?.promoUserSub ?? ''
    }));
  }
  if (response?.data?.redirectOverlay?.length) {
    thunkAPI.dispatch(setRedirectOverlay({
      promoCodeRedirectOverlay: response.data.redirectOverlay,
      promoCodeRedirectOverlayCountdown: response.data.redirectOverlayCountdown
    }));
  } else {
    thunkAPI.dispatch(setRedirectOverlay({
      promoCodeRedirectOverlay: undefined,
      promoCodeRedirectOverlayCountdown: undefined
    }));
  }

  return response.data;
})

export const paymentTokenFetch = createAsyncThunk('cart/paymentTokenFetch', async (payload: {
  orderId: string,
  enabledPayments: string[] | undefined,
}, thunkAPI) => {

  var config: AxiosRequestConfig = {
    method: 'post',
    url: "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/order-generate-token",
    headers: undefined,
    data: JSON.stringify(
    {
      "orderId": payload.orderId,
      "enabledPayments": payload.enabledPayments,
    })
  };

  let response = await axios(config);

  return response.data;
})

export const getCourierOptions = createAsyncThunk('cart/getCourierOptions', async (arg: { dest: number, weight: number }, thunkAPI) => {

  const response = await axios({
      method: 'post',
      url: "https://8mav2hp6ki.execute-api.ap-southeast-1.amazonaws.com/OrderAPIProduction/kirimin-aja-proxy",
      data: JSON.stringify({
        "axiosConfig": {
          "method": "post",
          "url": "https://client.kiriminaja.com/api/mitra/shipping_price",
          "data": {
            "origin": 6315,
            "destination": arg.dest,
            "weight": arg.weight
          }
        }
      })
  });

  return response.data;
})

export const getShipmentPrice = createAsyncThunk('cart/getShipmentPrice', async (destination: any) => {
  var data = {
    "origin":"CGK",
    "destination":destination.destination_code,
    "weight":"0.8",
    "p":"30",
    "l":"20",
    "t":"3",
  };

  var config: AxiosRequestConfig = {
    method: 'post',
    url: 'https://content-main-api-production.sicepat.com/public/delivery-fee/fare-non-international',
    headers: { 
      'authority': 'content-main-api-production.sicepat.com', 
      'pragma': 'no-cache', 
      'cache-control': 'no-cache', 
      'sec-ch-ua': '"Google Chrome";v="93", " Not;A Brand";v="99", "Chromium";v="93"', 
      'accept': 'application/json, text/plain, */*', 
      'content-type': 'application/json;charset=UTF-8', 
      'sec-ch-ua-mobile': '?0', 
      'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/93.0.4577.82 Safari/537.36', 
      'sec-ch-ua-platform': '"macOS"', 
      'origin': 'https://www.sicepat.com', 
      'sec-fetch-site': 'same-site', 
      'sec-fetch-mode': 'cors', 
      'sec-fetch-dest': 'empty', 
      'referer': 'https://www.sicepat.com/', 
      'accept-language': 'en-US,en;q=0.9,id-ID;q=0.8,id;q=0.7'
    },
    data: data
  };

  const response = await axios(config);

  const regularShipment = response.data.sicepat.results.find((item: any) => item.service === "REG");

  sendWebsiteEventMessage("Getting shipment estimate SiCepat" + JSON.stringify(regularShipment));

  return {
    regularShipment: regularShipment,
    destinationObject: destination,
  }
})

export function getMeasurementForCartStateItem(cartStateItemObject: CartStateItem,
  userBodyMeasurementsDict: {[key: string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]:{[key: string]: UserClothTypeSpecificBodyMeasurements[]};}) {

  const key2 = cartStateItemObject.clothingType + "-" + cartStateItemObject.fitType;
  var measurementsForItem: BodyMeasurements | undefined;
  if (cartStateItemObject.sizeType === 'personal') {
    if (cartStateItemObject.measurementIsBodyMeasurement) {
      // USE LATEST INSTEAD OF CORRECT VERSION
      let selectedObjectUpdateDateTime = userBodyMeasurementsDict[cartStateItemObject.measurementCreationDateTime][0];
      // for (let candidateObject of userBodyMeasurementsDict[cartStateItemObject.measurementCreationDateTime]) {
      //   if (candidateObject.updateDateTime === cartStateItemObject.measurementUpdateDateTime) {
      //     selectedObjectUpdateDateTime = candidateObject;
      //   }
      // }

      measurementsForItem = CalculateClothBodyMeasurementsFromBodyMeasurements(cartStateItemObject.clothingType, 
        cartStateItemObject.fitType, 
        selectedObjectUpdateDateTime.adjustedMeasurements as any, RegularSizeValue.L, []);

      if (measurementsForItem === undefined) {
        measurementsForItem = CalculateClothBodyMeasurementsFromBodyMeasurements(cartStateItemObject.clothingType, 
          cartStateItemObject.fitType, 
          selectedObjectUpdateDateTime.generatedMeasurements as any, RegularSizeValue.L, []);
      }
    } else {
      let selectedObjectUpdateDateTime = userClothingMeasurementsDict[key2][cartStateItemObject.measurementCreationDateTime][0];
      for (let candidateObject of userClothingMeasurementsDict[key2][cartStateItemObject.measurementCreationDateTime]) {
        if (candidateObject.updateDateTime === cartStateItemObject.measurementUpdateDateTime) {
          selectedObjectUpdateDateTime = candidateObject;
        }
      }
      measurementsForItem = selectedObjectUpdateDateTime.adjustedMeasurements;

      if (measurementsForItem === undefined) {
        measurementsForItem = selectedObjectUpdateDateTime.generatedMeasurements;
      }
    }
  } else if (cartStateItemObject.sizeType === 'regular') {
    measurementsForItem = BodyMeasurementsForSize[cartStateItemObject.regularSize];
  }

  if (measurementsForItem === undefined) {
    measurementsForItem = {};
  }

  measurementsForItem = {...measurementsForItem};

  // BACKWARDS COMPATIBILITY FIX - Has panjang lengan pendek but not lebar lengan pendek (INCOMPLETE CB)
  if (measurementsForItem['panjangLenganPendek'] && measurementsForItem['lebarLenganPendek'] === undefined) {
    measurementsForItem = CalculateClothBodyMeasurementsFromBodyMeasurements(cartStateItemObject.clothingType, cartStateItemObject.fitType,
      measurementsForItem, RegularSizeValue.L, [BodyMeasurementsKey.lebarLenganPendek]);
  }

  return measurementsForItem;
}

export function getRequestDataProducts(cartProductList: CartStateItem[], 
  userBodyMeasurementsDict: {[key: string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]:{[key: string]: UserClothTypeSpecificBodyMeasurements[]};}) {

  var requestDataProducts = [];

  for (var i = 0; i < cartProductList.length; i = i + 1) {
    var cartStateItemObject = cartProductList[i];

    for (var j = 0; j < cartProductList[i].amount; j = j + 1) {
      requestDataProducts.push({
        resource: "PRODUCT",
        resourceId: cartStateItemObject.productId, 
        provisionStrategy: "PRODUCT",
        provisionStrategyMetadata: {
          productId: cartStateItemObject.productId,
          measurements: getMeasurementForCartStateItem(cartStateItemObject, userBodyMeasurementsDict, userClothingMeasurementsDict),
          measurementsSizeShortName: cartStateItemObject.sizeType === SizeType.regular ? RegularSizeValueShortName?.[cartStateItemObject.regularSize] : '',
        }
      });
    }
  }

  return requestDataProducts;
}

export const postCheckCurrentCartAvailability = createAsyncThunk('cart/postCheckCurrentCartAvailability', async (payload: {}, thunkAPI) => {

  const rootState = (thunkAPI.getState() as RootState);
  const userBodyMeasurementsDict = rootState.userMeasurementsList.userBodyMeasurementsDictionary;
  const userClothingMeasurementsDict = rootState.userMeasurementsList.userClothingMeasurementsDictionary;

  const cartProductList = rootState.cart.cartProductList;

  var requestDataProducts = getRequestDataProducts(cartProductList, userBodyMeasurementsDict, userClothingMeasurementsDict);

  console.log("Fetch new");
  console.log(JSON.stringify(requestDataProducts, null, 4));

  var config: AxiosRequestConfig = {
    method: 'post',
    url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/availability',
    data: JSON.stringify(
      {
        "requestData": requestDataProducts,
        // Offline "true stock" mode: judge on provisioned - hutang, matching submitOrderRequest.
        ...(getOfflineTrueStock() ? { trueStockCheck: true } : {}),
      })
  };

  const response = await retryGetRequest(config, 8);

  sendWebsiteEventMessage("Checked availability for current cart items " + JSON.stringify(requestDataProducts) + " and result is " + response?.data?.provisionStatus);

  return {
    canBeProvisioned: response?.data?.provisionStatus === "ENOUGH",
  }
})

export const postCanAddItemCount = createAsyncThunk('cart/postCanAddItemCount', async (payload: CartStateItem, thunkAPI) => {

  const rootState = (thunkAPI.getState() as RootState);
  const userBodyMeasurementsDict = rootState.userMeasurementsList.userBodyMeasurementsDictionary;
  const userClothingMeasurementsDict = rootState.userMeasurementsList.userClothingMeasurementsDictionary;

  const cartProductList = rootState.cart.cartProductList;

  const hashKey = hash(payload);

  console.log(JSON.stringify(payload)); // TODO TESTING NOW FOR DARI BAJU FAVORIT

  var requestDataProducts = getRequestDataProducts(cartProductList, userBodyMeasurementsDict, userClothingMeasurementsDict);

  // New potential item
  var newCartStateItemMeasurement = getMeasurementForCartStateItem(payload, userBodyMeasurementsDict, userClothingMeasurementsDict);

  requestDataProducts.push({
    resource: "PRODUCT",
    resourceId: payload.productId, 
    provisionStrategy: "PRODUCT",
    provisionStrategyMetadata: {
      productId: payload.productId,
      measurements: newCartStateItemMeasurement,
      measurementsSizeShortName: payload.sizeType === SizeType.regular ? RegularSizeValueShortName?.[payload.regularSize] : '',
    }
  });

  console.log("Fetch new");
  console.log(JSON.stringify(requestDataProducts, null, 4));

  var config: AxiosRequestConfig = {
    method: 'post',
    url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/availability',
    data: JSON.stringify(
      {
        "requestData": requestDataProducts,
        // Offline "true stock" mode: judge on provisioned - hutang, matching submitOrderRequest.
        ...(getOfflineTrueStock() ? { trueStockCheck: true } : {}),
      })
  };

  const response = await retryGetRequest(config, 8);

  sendWebsiteEventMessage("Checked availability for " + JSON.stringify(requestDataProducts) + " and result is " + response?.data?.provisionStatus);
  
  const measurements = response?.data?.additionalRequestResultData?.measurements;
  const measurementsResult = response?.data?.additionalRequestResultData?.measurementsResult;

  let isServiceable = true;
  for (let objKey of Object.keys(measurementsResult)) { // Check if there is any value change
    if (measurements[objKey] !== measurementsResult[objKey]) {
      isServiceable = false;
    }
  }

  return {
    cartStateHash: hashKey,
    canBeProvisioned: response?.data?.provisionStatus === "ENOUGH",
    isServiceable: isServiceable,
    currentMeasurements: measurements,
    serviceableMeasurements: measurementsResult,
  }
})

export const loadAllIframeVariablesFromLocalStorage = createAsyncThunk('cart/loadAllIframeVariablesFromLocalStorage', async (payload, thunkAPI) => {
  thunkAPI.dispatch(loadCartFromLocalStorage());
  thunkAPI.dispatch(loadMeasurementsFromLocalStorage());
});

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItemCount: (state, action: PayloadAction<{ productId: string, 
      productDetail: ProductOnUser, fitType: FitType, sizeType: SizeType,
      measurementCreationDateTime: string, measurementUpdateDateTime: string, measurementIsBodyMeasurement: boolean, regularSize: RegularSizeValue }>) => {
      const { productId, productDetail, fitType, sizeType, measurementCreationDateTime, measurementUpdateDateTime, measurementIsBodyMeasurement, regularSize } = action.payload;
      if (measurementCreationDateTime === '') {
        return;
      }
      var cartStateItem: CartStateItem = {
        productId: productId,
        amount: 1,
        clothingType: productDetail.clothingType,
        fitType: fitType,
        sizeType: sizeType,
        measurementCreationDateTime: measurementCreationDateTime,
        measurementUpdateDateTime: measurementUpdateDateTime,
        measurementIsBodyMeasurement: measurementIsBodyMeasurement,
        regularSize: regularSize,
      }
      if (canAddProductAmountToCart(state, cartStateItem) !== "ENOUGH") {
        console.log("Can't add product to cart: not enough.")
        return;
      }
      const existingCartItem = state.cartProductList
      .find(item => item.productId === productId 
        && item.fitType === fitType
        && item.sizeType === sizeType
        && item.measurementCreationDateTime === measurementCreationDateTime
        && item.measurementUpdateDateTime === measurementUpdateDateTime
        && item.measurementIsBodyMeasurement === measurementIsBodyMeasurement
        && item.regularSize === regularSize);
      if (existingCartItem) {
        existingCartItem.amount = existingCartItem.amount + 1;
      } else {
        state.cartProductList.push(cartStateItem);
      }
      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    removeItemCount: (state, action: PayloadAction<{productId: string, 
      fitType: FitType, sizeType: SizeType,
      measurementCreationDateTime: string, measurementUpdateDateTime: string, regularSize: RegularSizeValue}>) => {
      const { productId, measurementCreationDateTime, measurementUpdateDateTime, fitType, sizeType, regularSize } = action.payload;
      const existingCartItem = state.cartProductList
      .find(item => item.productId === productId 
        && item.fitType === fitType
        && item.sizeType === sizeType
        && item.measurementCreationDateTime === measurementCreationDateTime
        && item.measurementUpdateDateTime === measurementUpdateDateTime
        && item.regularSize === regularSize);
      if (existingCartItem) {
        existingCartItem.amount = existingCartItem.amount - 1;
        if (existingCartItem.amount === 0) {
          state.cartProductList = state.cartProductList.filter(item => !(item.productId === productId
            && item.sizeType === sizeType
            && item.measurementCreationDateTime === measurementCreationDateTime
            && item.measurementUpdateDateTime === measurementUpdateDateTime
            && item.regularSize === regularSize));
        }
      }
      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    removeItem: (state, action: PayloadAction<{productId: string, 
      fitType: FitType, sizeType: SizeType,
      measurementCreationDateTime: string, measurementUpdateDateTime: string, regularSize: RegularSizeValue}>) => {
      const { productId, measurementCreationDateTime, measurementUpdateDateTime, fitType, sizeType, regularSize } = action.payload;
      state.cartProductList = state.cartProductList.filter(item => !(item.productId === productId 
        && item.fitType === fitType
        && item.sizeType === sizeType
        && item.measurementCreationDateTime === measurementCreationDateTime
        && item.measurementUpdateDateTime === measurementUpdateDateTime
        && item.regularSize === regularSize));
      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    updateAddressInOrder: (state, action: PayloadAction<Address>) => {
      Object.assign(state, action.payload);
    },
    updateCourierInOrder: (state, action: PayloadAction<{ 
      shipmentPrice: number, 
      shipmentPricePaid: number, 
      estimasiPengiriman: string, 
      courierSelectionObject: any, 
      courierWeight: number,
    }>) => {
      Object.assign(state, action.payload);
    },
    resetCourier: (state) => {
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
    },
    resetCartState: (state) => {
      state.cartProductList = [];
      state.orderData = undefined;
      state.orderStatus = 'unsubmitted';
      state.addressName = undefined;
      state.address = undefined;
      state.provinsi = undefined;
      state.kabupaten = undefined;
      state.kecamatan = undefined;
      state.kodePos = undefined;
      state.kotaKecamatanText = undefined;
      state.keterangan = undefined;
      state.phoneNumber = undefined;
      state.destinationObject = undefined;
      state.destinationKiriminAjaObject = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.couponKey = undefined;
      state.coupon = undefined;

      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    resetCartStateAndSetOrderStatus: (state, action: PayloadAction<"unsubmitted" | "waitingForPayment" 
    | "verifyPaymentWithServer" | "success" | "paymentFailed" | "rejected">) => {
      state.cartProductList = [];
      state.orderData = undefined;
      state.orderStatus = action.payload;
      state.addressName = undefined;
      state.address = undefined;
      state.provinsi = undefined;
      state.kabupaten = undefined;
      state.kecamatan = undefined;
      state.kodePos = undefined;
      state.kotaKecamatanText = undefined;
      state.keterangan = undefined;
      state.phoneNumber = undefined;
      state.destinationObject = undefined;
      state.destinationKiriminAjaObject = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
      state.couponKey = undefined;
      state.coupon = undefined;

      localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

      // Reset add item dict every time we add a new item
      state.canAddItemDict = {};
      state.cartProductListHash = hash(state.cartProductList);
    },
    resetCartAddressAndPaymentState: (state) => {
      state.orderData = undefined;
      state.orderStatus = 'unsubmitted';
      state.addressName = undefined;
      state.address = undefined;
      state.provinsi = undefined;
      state.kabupaten = undefined;
      state.kecamatan = undefined;
      state.kodePos = undefined;
      state.kotaKecamatanText = undefined;
      state.keterangan = undefined;
      state.phoneNumber = undefined;
      state.destinationObject = undefined;
      state.destinationKiriminAjaObject = undefined;
      state.courierSelectionObject = undefined;
      state.courierWeight = undefined;
      state.shipmentPrice = undefined;
      state.shipmentPricePaid = undefined;
      state.estimasiPengiriman = undefined;
    },
    updateOrderState (state, action) {
      state.orderStatus = action.payload;
    },
    updateUserTrigger (state, action) {
      state.userTriggerCheckout = action.payload;
    },
    removeCurrentCoupon (state) {
      state.couponKey = undefined;
      state.coupon = undefined;
    },
    removePaymentToken(state) {
      state.paymentToken = "";
    },
    setProductSpotlightList(state, action) {
      state.productSpotlightList = action.payload;
    },
    setLocalCoupon(state, action) {
      state.couponKey = action.payload.couponKey;
      state.coupon = action.payload.coupon;

      localStorage.setItem('coupon', JSON.stringify(state.coupon));
    },
    loadCartFromLocalStorage: (state) => {
      const storedCart = localStorage.getItem('cartProductList');
      if (storedCart) {
        try {
          const parsed = JSON.parse(storedCart);
          state.cartProductList = parsed;
          state.cartProductListHash = hash(parsed);

          // Clear the availability cache because the base cart has changed.
          state.canAddItemDict = {};

        } catch (err) {
          console.error("Failed to parse stored cart:", err);
        }
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(submitCouponRequest.pending, (state, action) => {
        state.couponFetchStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(submitCouponRequest.fulfilled, (state, action) => {
        state.couponFetchStatus = APIRequestStatus.Success;
        Mixpanel.track('couponKeyValue', {
          'couponKey': action.payload.couponKey,
          'coupon': action.payload.coupon,
        })
        console.log(action.payload);
        if (action.payload.isUsable) {
          state.couponKey = action.payload.couponKey;
          state.coupon = action.payload.coupon;
        }
        localStorage.setItem('coupon', JSON.stringify(action.payload.coupon));
      })
      .addCase(submitCouponRequest.rejected, (state, action) => {
        state.couponFetchStatus = APIRequestStatus.Failure;
      })
      .addCase(getShipmentPrice.pending, (state, action) => {
      })
      .addCase(getShipmentPrice.fulfilled, (state, action) => {
        state.shipmentPrice = action.payload.regularShipment.tariff;
        state.estimasiPengiriman = action.payload.regularShipment.etd;
        state.destinationObject = action.payload.destinationObject;
        console.log(action.payload);
      })
      .addCase(getShipmentPrice.rejected, (state, action) => {
      })
      .addCase(submitOrderRequest.pending, (state, action) => {
      })
      .addCase(submitOrderRequest.fulfilled, (state, action) => {
        // Checking results of order request
        console.log("Check results of order request");
        if (action.payload.creationDateTime === undefined || 
          action.payload.transactionToken === undefined) {
          // Order creation has failed. Display error message, then redirect to home page.
          console.log("Order request is rejected");
          state.orderData = action.payload;
          state.orderStatus = 'rejected';
        } else {
          // Order creation success
          state.orderData = action.payload;
          state.orderStatus = 'waitingForPayment';
        }
      })
      .addCase(submitOrderRequest.rejected, (state, action) => {
      })
      .addCase(getCurrentOrder.pending, (state, action) => {
      })
      .addCase(getCurrentOrder.fulfilled, (state, action) => {
        console.log("Received current order status.");
        console.log(action.payload);
        if (action.payload.paymentStatus === 'pending' 
        || action.payload.paymentStatus === 'capture'
        || action.payload.paymentStatus === 'settlement') {
          state.orderStatus = 'success';
          state.orderData = undefined;
          state.addressName = undefined;
          state.address = undefined;
          state.provinsi = undefined;
          state.kabupaten = undefined;
          state.kecamatan = undefined;
          state.kodePos = undefined;
          state.kotaKecamatanText = undefined;
          state.keterangan = undefined;
          state.phoneNumber = undefined;
          state.destinationObject = undefined;
          state.destinationKiriminAjaObject = undefined;
          state.courierSelectionObject = undefined;
          state.courierWeight = undefined;
          state.shipmentPrice = undefined;
          state.shipmentPricePaid = undefined;
          state.estimasiPengiriman = undefined;
          state.couponKey = undefined;
          state.coupon = undefined;
          state.cartProductList = [];

          localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));

          // Reset add item dict every time we add a new item
          state.canAddItemDict = {};
          state.cartProductListHash = hash(state.cartProductList);
        } else {
          console.log("PAYMENT ERROR");
          state.orderStatus = 'paymentFailed';
          state.orderData = undefined;
          state.addressName = undefined;
          state.address = undefined;
          state.provinsi = undefined;
          state.kabupaten = undefined;
          state.kecamatan = undefined;
          state.kodePos = undefined;
          state.kotaKecamatanText = undefined;
          state.keterangan = undefined;
          state.phoneNumber = undefined;
          state.destinationObject = undefined;
          state.destinationKiriminAjaObject = undefined;
          state.courierSelectionObject = undefined;
          state.courierWeight = undefined;
          state.shipmentPrice = undefined;
          state.shipmentPricePaid = undefined;
          state.estimasiPengiriman = undefined;
          state.couponKey = undefined;
          state.coupon = undefined;
          state.estimasiPengiriman = undefined;
        }
      })
      .addCase(getCurrentOrder.rejected, (state, action) => {
      })
      .addCase(postCanAddItemCount.pending, (state, action) => {
        var cartStateItemHash = hash(action.meta.arg);
        state.canAddItemDict[cartStateItemHash] = "LOADING";
      })
      .addCase(postCheckCurrentCartAvailability.fulfilled, (state, action) => {
        if (!action.payload.canBeProvisioned) {
          // If it is not available anymore, we reset the contents
          state.cartProductList = [];
          state.orderData = undefined;
          state.orderStatus = 'unsubmitted';
          state.addressName = undefined;
          state.address = undefined;
          state.provinsi = undefined;
          state.kabupaten = undefined;
          state.kecamatan = undefined;
          state.kodePos = undefined;
          state.kotaKecamatanText = undefined;
          state.keterangan = undefined;
          state.phoneNumber = undefined;
          state.destinationObject = undefined;
          state.destinationKiriminAjaObject = undefined;
          state.courierSelectionObject = undefined;
          state.courierWeight = undefined;
          state.shipmentPrice = undefined;
          state.shipmentPricePaid = undefined;
          state.estimasiPengiriman = undefined;
          state.couponKey = undefined;
          state.coupon = undefined;
    
          localStorage.setItem('cartProductList', JSON.stringify(state.cartProductList));
    
          // Reset add item dict every time we add a new item
          state.canAddItemDict = {};
          state.cartProductListHash = hash(state.cartProductList);

          console.log("Cart contents are unavailable. Emptying.");
        } else {
          console.log("Cart contents are safe / available. Carry on.");
        }
      })
      .addCase(postCheckCurrentCartAvailability.rejected, (state, action) => {
      })
      .addCase(postCheckCurrentCartAvailability.pending, (state, action) => {
        // We put the hash in, so it is not calculated / trigerred twice
        state.canAddItemDict = {};
        state.cartProductListHash = hash(state.cartProductList);
      })
      .addCase(postCanAddItemCount.fulfilled, (state, action) => {
        state.canAddItemDict[action.payload.cartStateHash] = {
          canBeProvisioned: action.payload.canBeProvisioned,
          isServiceable: action.payload.isServiceable,
          currentMeasurements: action.payload.currentMeasurements,
          serviceableMeasurements: action.payload.serviceableMeasurements,
        };
      })
      .addCase(postCanAddItemCount.rejected, (state, action) => {
      })
      .addCase(paymentTokenFetch.pending, (state, action) => {
        state.paymentTokenFetchStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(paymentTokenFetch.fulfilled, (state, action) => {
        state.paymentTokenFetchStatus = APIRequestStatus.Success;
        if (action.payload.success) {
          state.paymentToken = action.payload.transactionToken;
        }
      })
      .addCase(paymentTokenFetch.rejected, (state, action) => {
        state.paymentTokenFetchStatus = APIRequestStatus.Failure;
      })
      .addCase(postCustomerNotification.pending, (state, action) => {
      })
      .addCase(postCustomerNotification.fulfilled, (state, action) => {
      })
      .addCase(postCustomerNotification.rejected, (state, action) => {
      })
      .addCase(cancelUserOrders.pending, (state, action) => {
      })
      .addCase(cancelUserOrders.fulfilled, (state, action) => {
      })
      .addCase(cancelUserOrders.rejected, (state, action) => {
      })
      .addCase(getCourierOptions.pending, (state, action) => {
        state.courierOptionsFetchStatus = APIRequestStatus.RequestInProgress;
      })
      .addCase(getCourierOptions.fulfilled, (state, action) => {
        state.courierOptionsFetchStatus = APIRequestStatus.Success;
        
        if (action.payload?.success && action.payload?.returnBody?.results) {
          state.courierOptions = action.payload.returnBody.results;
        }
      })
      .addCase(getCourierOptions.rejected, (state, action) => {
        state.courierOptionsFetchStatus = APIRequestStatus.Failure;
      })
  }
})

export function getDiscountedPriceAndTimestampInProductSpotlightList(productSpotlightList: ProductSpotlight[], productId: string): 
{
  discountedPrice: number
  timestampLimit: string
  alternativeSource?: 'FLASHSALE' | 'COUPON' // By default assumes 'FLASHSALE'
} {
  const timestampCurrent = new Date().toISOString();

  // Get all active deals
  let activeProductSpotlights = productSpotlightList.filter((spotlightObj) => 
    spotlightObj.productId === productId &&
    timestampCurrent < spotlightObj.timestampLimit && 
    timestampCurrent > spotlightObj.timestampStart);

  // Find the best one
  let currentLowestPrice = 1000000000;
  let spotlightObjectValue: ProductSpotlight | undefined = undefined;

  for (let spotlightObj of activeProductSpotlights) {
    if (spotlightObj.discountedPrice < currentLowestPrice) {
      currentLowestPrice = spotlightObj.discountedPrice;
      spotlightObjectValue = spotlightObj;
    }
  }

  // Return results
  return {
    discountedPrice: spotlightObjectValue?.discountedPrice ?? -1,
    timestampLimit: spotlightObjectValue?.timestampLimit ?? '1999-10-10T00:00:00Z',
    alternativeSource: spotlightObjectValue?.alternativeSource
  }
}

export function checkIfMeasKitSizeExists(cartState: CartState, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]} ) {

  let measKitSizeExists = false;

  for (let cartProductItem of cartState.cartProductList) {
    if (cartProductItem.sizeType === SizeType.personal
      && cartProductItem.measurementIsBodyMeasurement) {
      measKitSizeExists = true;
    }
  }

  return measKitSizeExists;
}

export interface CartItemsWithCouponPromotionsDict {
  [key:string]: {
    discountedPriceWithCouponPromotions: number
    amountDiscounted: number
  }
}

export function getCartItemsWithCouponPromotions(state: CartState, productListState: ProductsList, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}) {
  const cartItemsWithCouponPromotions: CartItemsWithCouponPromotionsDict = {};

  let mostExpensiveProductId = '';
  let mostExpensiveProductPrice = 0;

  // CALC ORIGINAL & SUBTOTAL AMOUNT
  for (let itemKey in state.cartProductList) {
    let item = state.cartProductList[itemKey];
    if (productListState.productsDictionary[item.productId] === undefined) {
      continue;
    }

    // Equals to -1 if not exist
    let defaultSalePrice = productListState.productsDictionary[item.productId].discountedPrice === -1 ? 
      productListState.productsDictionary[item.productId].originalPrice :
      productListState.productsDictionary[item.productId].discountedPrice;
  
    let combinedProductSpotlightList: ProductSpotlight[] = (state?.productSpotlightList?.length ? state.productSpotlightList : []).concat(state.coupon?.couponProductSpotlightList?.map((prodObj) => ({
      ...prodObj,
      timestampStart: '1999-10-10T00:00:00.000Z',
      timestampLimit: state.coupon?.expirationDateTime ?? '1999-10-10T00:00:00.000Z', 
      isMainSpotlight: false,
      alternativeSource: 'COUPON'
    })) ?? []);

    let spotlightDiscPrice = state.coupon?.couponBlastingEffects?.length ? Math.round((1 - state.coupon?.couponBlastingEffects?.[0].effectValue) * (productListState.productsDictionary[item.productId].originalPrice)) : combinedProductSpotlightList?.length ? getDiscountedPriceAndTimestampInProductSpotlightList(combinedProductSpotlightList, item.productId).discountedPrice : -1;

    if (spotlightDiscPrice !== -1) {
      continue; // Skip if flash sale price is applied
    }

    if (mostExpensiveProductPrice < defaultSalePrice) {
      mostExpensiveProductId = item.productId;
      mostExpensiveProductPrice = defaultSalePrice;
    }
  }

  // CALC DISC WITH PROMOTIONS AMOUNT
  if (state.couponKey && state.coupon) {
    // Check usability
    let couponUsable = true;

    if (state.coupon.expirationDateTime) {
      couponUsable = couponUsable && (new Date().toISOString() < state.coupon.expirationDateTime);
    }

    let allConditionsSatisfied = true;
    for (let couponConditionItem of state.coupon.couponConditions) {
      // if (couponConditionItem.conditionKey === "MEASKITSIZEEXISTS") {
      //   let measKitSizeExists = checkIfMeasKitSizeExists(state, userBodyMeasurementsDict);

      //   allConditionsSatisfied = allConditionsSatisfied && measKitSizeExists;
      // }
    }
    couponUsable = couponUsable && allConditionsSatisfied;

    // Use coupon
    if (couponUsable) {
      for (let key in state.coupon.couponEffects) {
        const couponEffect = state.coupon.couponEffects[key];
        if (couponEffect.effectKey === "SINGLEITEMDISCOUNT" && mostExpensiveProductId) {
          cartItemsWithCouponPromotions[mostExpensiveProductId] = {
            discountedPriceWithCouponPromotions: Math.round(mostExpensiveProductPrice * (1 - couponEffect.effectValue as number)),
            amountDiscounted: mostExpensiveProductPrice - Math.round(mostExpensiveProductPrice * (1 - couponEffect.effectValue as number)),
          }
        }
      }
    }
  }
  
  return cartItemsWithCouponPromotions;
}

export function GetBundlingKeyAmountDict(cartProductList: CartStateItem[], productListState: ProductsList) {
  const bundlingKeyAmountDict: {[key: string]: number } = {};

  for (let itemKey in cartProductList) {
    let item = cartProductList[itemKey];

    if (productListState.productsDictionary[item.productId]?.bundlingData) {
      const bundlingKey = productListState.productsDictionary[item.productId].bundlingData?.bundlingKey;
      if (bundlingKey) {
        bundlingKeyAmountDict[bundlingKey] = (bundlingKeyAmountDict?.[bundlingKey] ?? 0) + item.amount;
      }
    }
  }

  return bundlingKeyAmountDict;
}

export function CheckIsPlusSize(item: CartStateItem, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]: {
      [key: string]: UserClothTypeSpecificBodyMeasurements[];
  }}) {
    
    // Resolve the measurement object based on type
    let measurementObject;
    if (item.sizeType === 'personal') {
        if (item.measurementIsBodyMeasurement) {
             // Look in BODY Dict
            // USE LATEST INSTEAD OF CORRECT VERSION
             measurementObject = userBodyMeasurementsDict[item.measurementCreationDateTime]?.[0];
            //  measurementObject = userBodyMeasurementsDict[item.measurementCreationDateTime]?.filter((measObj: any) => measObj.updateDateTime === item.measurementUpdateDateTime)?.[0];
        } else {
             // Look in CLOTHING Dict
             measurementObject = userClothingMeasurementsDict[item.clothingType + '-' + item.fitType]?.[item.measurementCreationDateTime]?.filter((measObj: any) => measObj.updateDateTime === item.measurementUpdateDateTime)?.[0];
        }
    }

    return (
      (item.sizeType === 'regular' && 
        (item.regularSize === RegularSizeValue.XXXL || item.regularSize === RegularSizeValue.XXXXL || item.regularSize === RegularSizeValue.XXXXXL || item.regularSize === RegularSizeValue.XXXXXXL || item.regularSize === RegularSizeValue.XXXXXXXL || item.regularSize === RegularSizeValue.XXXXXXXXL))
        ||
        (item.sizeType === 'personal' && 
        measurementObject?.adjustedMeasurements?.lingkarLeher &&
        (measurementObject.adjustedMeasurements.lingkarLeher as number) > 45)
    );
}

export function calculateCompleteCartInvoice(state: CartState, productListState: ProductsList, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]: {
      [key: string]: UserClothTypeSpecificBodyMeasurements[];
  }}) {
  const resultObj: {
    cartItemsList: any[],
    totalAmountObj: {
      originalPriceAmount: number,
      discountedPriceAmount: number,
      discountedPriceAmountWithPromotions: number,
      discountedPriceAmountWithPromotionsAndShipment: number,
    },
    cartItemsWithCouponPromotions: CartItemsWithCouponPromotionsDict
  } = {
    cartItemsList: [],
    totalAmountObj: {
      originalPriceAmount: 0,
      discountedPriceAmount: 0,
      discountedPriceAmountWithPromotions: 0,
      discountedPriceAmountWithPromotionsAndShipment: 0,
    },
    cartItemsWithCouponPromotions: {}
  }

  // BUNDLING
  const bundlingKeyAmountDict = GetBundlingKeyAmountDict(state.cartProductList, productListState);

  // CALC ORIGINAL & SUBTOTAL AMOUNT
  // ORIGINAL: ORIGINAL PRICE TOTAL
  // DEFAULT DISCOUNT: DISCOUNTS BEFORE ANY PROMOTIONS (NOT WITH SPOTLIGHT, BUT THIS INCLUDES BUNDLING)
  // SUBTOTAL: WITH PROMOTIONS TOTAL
  let originalTotal = 0;
  let defaultDiscTotal = 0;
  let subTotal = 0;
  for (let itemKey in state.cartProductList) {
    let item = state.cartProductList[itemKey];
    if (productListState.productsDictionary[item.productId] === undefined) {
      continue;
    }

    // PLUS SIZE CHARGE CHECK
    const isPlusSize = CheckIsPlusSize(item, userBodyMeasurementsDict, userClothingMeasurementsDict);
    if (isPlusSize) {
      const extraSizeChargePrice = (productListState.productsDictionary[item.productId]?.extraSizeChargePrice ?? 0);
      originalTotal = originalTotal + item.amount * extraSizeChargePrice;
      subTotal = subTotal + item.amount * extraSizeChargePrice;
      defaultDiscTotal = defaultDiscTotal + item.amount * extraSizeChargePrice;
    }

    originalTotal = originalTotal + item.amount * productListState.productsDictionary[item.productId].originalPrice;

    // Equals to -1 if not exist
    let defaultDiscPrice = productListState.productsDictionary[item.productId].discountedPrice === undefined ? -1 :
      productListState.productsDictionary[item.productId].discountedPrice;

    // BUNDLING PRICE EFFECT
    if (productListState.productsDictionary[item.productId]?.bundlingData) {
      const bundlingData = productListState.productsDictionary[item.productId].bundlingData;
      const bundlingKey = bundlingData?.bundlingKey;
      if (bundlingKey && bundlingKeyAmountDict[bundlingKey] > 1) {
        defaultDiscPrice = 
          bundlingKeyAmountDict[bundlingKey] >= 3 ? bundlingData.bundlingPrice3 :
          bundlingKeyAmountDict[bundlingKey] >= 2 ? bundlingData.bundlingPrice2 :
          defaultDiscPrice;
      }
    }
    
    let combinedProductSpotlightList: ProductSpotlight[] = (state?.productSpotlightList?.length ? state.productSpotlightList : []).concat(state.coupon?.couponProductSpotlightList?.map((prodObj) => ({
      ...prodObj,
      timestampStart: '1999-10-10T00:00:00.000Z',
      timestampLimit: state.coupon?.expirationDateTime ?? '1999-10-10T00:00:00.000Z', 
      isMainSpotlight: false,
      alternativeSource: 'COUPON'
    })) ?? []);

    // TODO: TEMPORARY FIX 
    let spotlightDiscPrice = state.coupon?.couponBlastingEffects?.length ? Math.round((1 - state.coupon?.couponBlastingEffects?.[0].effectValue) * (productListState.productsDictionary[item.productId].originalPrice)) : combinedProductSpotlightList?.length ? getDiscountedPriceAndTimestampInProductSpotlightList(combinedProductSpotlightList, item.productId).discountedPrice : -1;
    let thereIsDiscountedPrice = defaultDiscPrice !== -1 || spotlightDiscPrice !== -1;

    if (thereIsDiscountedPrice) {
      let discountedPrice = spotlightDiscPrice !== -1 ? spotlightDiscPrice : defaultDiscPrice;
      subTotal = subTotal + item.amount * discountedPrice;
    } else {
      subTotal = subTotal + item.amount * productListState.productsDictionary[item.productId].originalPrice;
    }

    defaultDiscTotal = defaultDiscTotal + item.amount * (defaultDiscPrice !== -1 ? defaultDiscPrice : productListState.productsDictionary[item.productId].originalPrice);
  }

  // PUT IN ORIGINAL & SUBTOTAL AMOUNT
  resultObj.totalAmountObj.originalPriceAmount = originalTotal;
  resultObj.totalAmountObj.discountedPriceAmount = defaultDiscTotal;

  // CHECK ADDITIONAL DISCOUNT FOR COUPON EFFECT SINGLEITEMDISCOUNT
  const cartItemsWithCouponPromotions = getCartItemsWithCouponPromotions(state, productListState, userBodyMeasurementsDict);
  resultObj.cartItemsWithCouponPromotions = cartItemsWithCouponPromotions;

  // CALC DISC WITH PROMOTIONS AMOUNT
  if (state.couponKey && state.coupon) {
    // Check usability
    let couponUsable = true;

    if (state.coupon.expirationDateTime) {
      couponUsable = couponUsable && (new Date().toISOString() < state.coupon.expirationDateTime);
    }

    let allConditionsSatisfied = true;
    for (let couponConditionItem of state.coupon.couponConditions) {
      if (couponConditionItem.conditionKey === "MEASKITSIZEEXISTS") {
        let measKitSizeExists = checkIfMeasKitSizeExists(state, userBodyMeasurementsDict);

        allConditionsSatisfied = allConditionsSatisfied && measKitSizeExists;
      }
    }
    couponUsable = couponUsable && allConditionsSatisfied;

    // Use coupon
    if (couponUsable) {
      for (let key in state.coupon.couponEffects) {
        const couponEffect = state.coupon.couponEffects[key];
        if (couponEffect.effectKey === "PRICECUT") {
          subTotal -= couponEffect.effectValue as number;
        }
        if (couponEffect.effectKey === "PRICECUTPERCENT") {
          subTotal = subTotal * (1 - couponEffect.effectValue as number);
        }
        if (couponEffect.effectKey === "SINGLEITEMDISCOUNT") {
          subTotal = subTotal - (cartItemsWithCouponPromotions?.[Object.keys(cartItemsWithCouponPromotions)?.[0]]?.amountDiscounted ?? 0);
        }
      }
    }
  }

  // PUT IN DISC WITH PROMOTIONS AMOUNT
  resultObj.totalAmountObj.discountedPriceAmountWithPromotions = subTotal;

  // CALC DISC WITH PROMOTIONS AMOUNT AND SHIPMENT
  if (state.shipmentPrice) {
    let endShipmentPrice = getEndShipmentPrice(subTotal, getNumberOfItems(state), state.shipmentPrice, state?.couponKey ?? "");
    subTotal = subTotal + (endShipmentPrice as number);
  }
  
  // PUT IN DISC WITH PROMOTIONS AMOUNT AND SHIPMENT
  resultObj.totalAmountObj.discountedPriceAmountWithPromotionsAndShipment = subTotal;

  return resultObj;
}

export function getDiscountedPriceAmountWithPromotionsAndShipment(state: CartState, productListState: ProductsList, 
  userBodyMeasurementsDict: {[key:string]: UserBodyMeasurements[]}, 
  userClothingMeasurementsDict: {[key: string]: {
      [key: string]: UserClothTypeSpecificBodyMeasurements[];
  }}) {
  let resultObj = calculateCompleteCartInvoice(state, productListState, userBodyMeasurementsDict, userClothingMeasurementsDict);

  return resultObj.totalAmountObj.discountedPriceAmountWithPromotionsAndShipment;
}

export function getNumberOfItems(state: CartState) {
  let total = 0;
  for (let itemKey in state.cartProductList) {
    total += state.cartProductList[itemKey].amount;
  }
  return total;
}

export function getShipmentPriceSubsidyAmount(numberOfItemsInCart: number, cartValue: number, couponKey: string) {

  if (couponKey === "FIRSTUSER" || couponKey?.includes("STORE")) {
    return (
      numberOfItemsInCart === 0 ? 0 : 
      numberOfItemsInCart === 1 ? 19000 : 
      numberOfItemsInCart === 2 ? 31000 : 
      numberOfItemsInCart === 3 ? 42000 : 
      numberOfItemsInCart === 4 ? 56000 : 0
    )
  }

  return (
    numberOfItemsInCart === 0 ? 0 : 
    numberOfItemsInCart === 1 && cartValue > 200000 ? 14000 : 
    numberOfItemsInCart === 1 ? 7000 : 
    numberOfItemsInCart === 2 ? 19000 : 
    numberOfItemsInCart === 3 ? 31000 : 
    numberOfItemsInCart === 4 ? 42000 : 
    numberOfItemsInCart >= 5 ? 56000 : 0
  )
}

export function getEndShipmentPrice(totalCartAmount: number, numberOfItemsInCart: number, initialShipmentPrice: number | undefined, couponKey: string) {
  if (!initialShipmentPrice || initialShipmentPrice === -1) {
    return initialShipmentPrice;
  }

  let endCartShipmentPrice = initialShipmentPrice;
  let subsidyAmount = getShipmentPriceSubsidyAmount(numberOfItemsInCart, totalCartAmount, couponKey);

  endCartShipmentPrice = endCartShipmentPrice > subsidyAmount ? endCartShipmentPrice - subsidyAmount : 0;
  
  return endCartShipmentPrice;
}

export function getCurrentShipmentPromo() {
  const month = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus",
  "September","Oktober","November","Desember"];
  
  const d = new Date();
  
  const shipmentPromoObject = {
    bulan: month[d.getMonth()],
    tahun: d.getFullYear(),
    bagianBulan: d.getDate() > 15 ? "akhir" : "launch",
    tanggalAkhir: d.getDate() > 15 ? (new Date(d.getFullYear(), d.getMonth() + 1, 0)).getDate() : 15,
  }

  return shipmentPromoObject;
}

export function getAddToCartNextSteps(product: ProductOnUser, measurement: UserBodyMeasurements | UserClothTypeSpecificBodyMeasurements) {
  const nextSteps: string[] = []; // if there is no next steps, the product can be added to cart right away.

  // TODO:
  // Actual consideration and steps to be done, determined by the product details
  // and the measurement requirements it has.
  return nextSteps;
}

export function getMeasurementsUsedInCart(state: CartState) {
  let results: {
    clothingType: ClothingType,
    fitType: FitType,
    creationDateTime: string,
  }[] = [];

  for (let key in state.cartProductList) {
    const cartItem = state.cartProductList[key];
    results.push({
      clothingType: cartItem.clothingType,
      fitType: cartItem.fitType,
      creationDateTime: cartItem.measurementCreationDateTime,
    })
  }

  return results;
}

// Returns "ENOUGH", "NOTENOUGH", or "LOADING"
export function canAddProductAmountToCart(state: CartState, cartStateItem: CartStateItem): "ENOUGH" | "NOTENOUGH" | "LOADING" | undefined {

  var cartStateItemHash = hash(cartStateItem);

  if (state.canAddItemDict[cartStateItemHash] !== undefined) {
    if (state.canAddItemDict[cartStateItemHash] === "LOADING") {
      return "LOADING";
    } else if (typeof state.canAddItemDict[cartStateItemHash] === "object") {
      return (state.canAddItemDict[cartStateItemHash] as any).canBeProvisioned ? "ENOUGH" : "NOTENOUGH";
    } else {
      return "LOADING";
    }

  } else {
    return "LOADING";
  }
}

// Action creators are generated for each case reducer function
export const { addItemCount, removeItemCount, removeItem, updateCourierInOrder, resetCourier,
  updateAddressInOrder, updateOrderState, resetCartState, resetCartStateAndSetOrderStatus,
  resetCartAddressAndPaymentState, updateUserTrigger, removeCurrentCoupon, removePaymentToken, setLocalCoupon, setProductSpotlightList, loadCartFromLocalStorage } = cartSlice.actions

export default cartSlice.reducer